import type { TeamWith } from "@redotech/account-common/base-team";
import { sortFulfillmentOrderLineItems } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import {
  FulfillmentOrderLineItem,
  LineItemLot,
} from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import { ItemOrderBy } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import type { OutboundLabelSettings } from "@redotech/redo-fulfillment-settings/outbound-label-settings";
import { Flex } from "@redotech/redo-web/flex";
import { toMoney } from "@redotech/redo-web/fx/to-money";
import {
  ItemTableHeader,
  ProductListRow,
} from "@redotech/redo-web/product-list-row";
import { memo, useMemo } from "react";
import { LineItemLots } from "./line-item-lots";
import { useProductPageHrefs } from "./product-page-hrefs";

export const ItemsTable = memo(function ItemsTable({
  items,
  team,
  disableTooltip = false,
  hideHeader = false,
  showBin = false,
  linkToProductPages = false,
}: {
  items: (FulfillmentOrderLineItem & { lots?: LineItemLot[] })[];
  team: TeamWith<{ outboundLabels: OutboundLabelSettings }> | undefined;
  disableTooltip?: boolean;
  hideHeader?: boolean;
  showBin?: boolean;
  linkToProductPages?: boolean;
}) {
  const sortedItems = useMemo(() => {
    return sortFulfillmentOrderLineItems(
      team?.settings?.outboundLabels?.documents?.pickList?.orderBy ??
        ItemOrderBy.QuantityDesc,
      items,
    ).filter((item) => item.quantity > 0);
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const productHrefs = useProductPageHrefs(
    linkToProductPages ? sortedItems.map((item) => item.redoProductId) : [],
  );

  const showCustomAttributes =
    !!team?.settings?.outboundLabels?.degroupItemsByCustomAttributes;
  const hideUnderscoreCustomAttributes =
    !!team?.settings?.outboundLabels?.hideUnderscoreCustomAttributes;

  return (
    <Flex
      borderColor="primary"
      borderRadius="md"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      gap="none"
    >
      {!hideHeader && (
        <ItemTableHeader flatTopCorners={false} showBin={showBin} />
      )}
      <Flex dir="column" flex="1" gap="none">
        {sortedItems.map((item, index) => (
          <Flex
            borderBottomWidth={index !== items.length - 1 ? "1px" : "none"}
            borderColor="secondary"
            borderStyle="solid"
            dir="column"
            gap="none"
            key={index}
            w="full"
          >
            <ProductListRow
              bin={showBin ? item.bin : undefined}
              customAttributes={
                showCustomAttributes
                  ? hideUnderscoreCustomAttributes
                    ? item.customAttributes?.filter(
                        (attr) => !attr.key.startsWith("_"),
                      )
                    : item.customAttributes
                  : undefined
              }
              disableTooltip={disableTooltip}
              imageUrl={item.imageUrl}
              price={item.unitDiscountedPrice.amount}
              priceMoney={toMoney(item.unitDiscountedPrice)}
              productHref={
                item.redoProductId
                  ? productHrefs.get(item.redoProductId)
                  : undefined
              }
              quantity={item.quantity}
              sku={item.sku}
              title={item.productTitle || item.title}
              variantTitle={item.variantTitle}
            />
            {item.lots?.length ? (
              <Flex pb="lg" px="xl" w="full">
                <LineItemLots lots={item.lots} />
              </Flex>
            ) : null}
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
});
